import React, { useEffect, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";

// Start value for `firstItemIndex`, must be larger than total possible prepended items
const FIRST_INDEX_OFFSET = 5000 
const BUTTON_ADVANCE_SIZE = 400

import objectImage from '../../../../images/new-icons/Icon_240_art.svg'

/**
 * @function formatImage
 * 
 * @param {Object} representation
 * @param {Number} width
 * 
 * Returns an image URL formatted for the lesser of `width` or native width
 **/ 
const formatImage = (representation, width) => {
  const { height: imageHeight, primary, width: imageWidth } = representation

  // imageWidth may be null, so only min-out if valued
  let maxWidth = width
  let height 

  if (imageWidth) {
    maxWidth = Math.min(width, imageWidth)
    height = Math.round(imageHeight * ( maxWidth / imageWidth ))
  }


  // NB: Must carry trailing '/' for correct URL() join
  const imageUrl = primary.replace('/square/!240,240/0/default.jpg','/')
  const formatString = `full/${maxWidth},/0/default.jpg` 
  const url = new URL(formatString, imageUrl)

  return { url: url.href, width: maxWidth, height }
}

export const CarouselItem = ({first, itemData, last, eager, selected}) => {
  const [loaded, setLoaded] = useState(false)

  if (!itemData) return "";

  const { _id, _source } = itemData
  const { caption, 
          cr_number, 
          representation, 
          label, 
          timespan } = _source ?? {};

  // Set placeholder images
  let imageSrcPrime = '/assets/' + objectImage
  let imageSrcDbl = '/assets/' + objectImage
  let height = 400
  let width = 400
  let doubleHeight = 800
  let doubleWidth = 800

  // Data is laoded and there is an image, get the double-density IIIF thumbnail
  if (representation?.primary) {
    const prime = formatImage(representation, 400)
    imageSrcPrime = prime.url
    height = prime.height
    width = prime.width

    const double = formatImage(representation, 800)
    imageSrcDbl = double.url
    doubleHeight = double.height
    doubleWidth = double.width
  }

  const title = label?.at(0) ?? ''
  const date = timespan?.label ?? ''
  const institution = caption?.owner?.at(0) ?? ''

  const linkHref = '/' + _id

  // Only remove the loading indicator if the data has loaded (not the stub pass)
  const onImageLoaded = (event) => {
    setLoaded(true)
  }

  return (
    <div className={`carousel-item ${selected ? "is-active" : ""}`}
         data-loaded={!!_source}
         data-load-id={_id} >
         <div className={loaded ? "" : "loading-image"} style={{ position: 'absolute' }}></div>
      <a href={ linkHref }>
        <picture >
          <source srcSet={imageSrcDbl}
                  media="(min-resolution: 2x)"
                  width={doubleWidth}
                  height={doubleHeight}></source>
          <img src={imageSrcPrime}
               alt=""
               loading={selected ? "eager" : "lazy"}
               width={width}
               height={height}
               onLoad={onImageLoaded}/>
        </picture>
      </a>
      <dl>
        <dt>CR No.&nbsp;</dt>
        <dd>{cr_number}</dd>
      </dl>
      <dl>
        <dt className="sr-only">Title</dt>
        <dd className="title"><a href={ linkHref }>{title}</a></dd>
      </dl>
      <dl>
        <dt className="sr-only">Date</dt>
        <dd className="year">{date}</dd>
      </dl>
      <dl>
        <dt className="sr-only">Institution</dt>
        <dd>{institution}</dd>
      </dl>
    </div>
  );
};

/**
 * @function CatalogCarousel
 * 
 **/ 
export const CatalogCarousel = ({ blockSize,
                                  catalog, 
                                  count, 
                                  loadMore,
                                  prepended, 
                                  selected,
                                  selectedIndex,
                                  setSelectedIndex, 
                                  setSelected}) => {
  const [visibleRange, setVisibleRange] = useState({ startIndex: 0, endIndex: 0 })
  const carousel = useRef()

  const prevClick = () => {
    carousel.current?.scrollBy({ top: -BUTTON_ADVANCE_SIZE, behavior: 'smooth' })
  }

  const nextClick = () => {
    carousel.current?.scrollBy({ top: BUTTON_ADVANCE_SIZE, behavior: 'smooth' })
  }

  const startReached = (index) => {
    loadMore('start')
  }

  const endReached = (index) => {
    loadMore('end')
  }

  return (
    <div className="catalog-carousel">
      <div className="catalog-carousel--control"></div>
      <div className="catalog-carousel--carousel">
        <div className="catalog-carousel--carousel__buttons"></div>
         <div className="gradient gradient-left">
            <button
              type="button"
              title="Previous"
              className="btn btn-link sliding-button sliding-button-left sliding-button-120 "
              onClick={prevClick} >
              <div className="icon icon-arrow-left-lg icon--black icon-lg">
                <span className="sr-only">Previous</span>
              </div>
            </button>
          </div>
          <Virtuoso
            className="carousel-items"
            data={catalog}
            ref={carousel}
            totalCount={count}
            rangeChanged={setVisibleRange}
            initialTopMostItemIndex={{ index: selectedIndex, align: 'center' }}
            startReached={startReached}
            endReached={endReached}
            firstItemIndex={FIRST_INDEX_OFFSET - prepended}
            style={{ height: 380, display: 'flex', columnGap: '3.5rem', alignItems: 'center' }}
            horizontalDirection
            itemContent={(_, cat) => (<CarouselItem selected={cat._id === selected} 
                                        itemData={cat} />)}
          />
        <div className="gradient gradient-right">
            <button
              type="button"
              title="Next"
              className="btn btn-link sliding-button sliding-button-right sliding-button-120 "
              onClick={nextClick} >
              <div className="icon icon-arrow-right-lg icon--black icon-lg">
                <span className="sr-only">Next</span>
              </div>
            </button>
          </div>
      </div>
    </div>
  );
};
